<template>
  <div v-if="hasViewingDetails" :class="$style.viewing" data-test="viewing-details">
    <h2>{{ t('viewingDetails.title') }}</h2>
    <slot />
    <div
      v-if="viewingNote"
      :class="$style.viewingNote"
      data-test="viewing-note"
      @click="handleViewingNoteClick"
      v-html="viewingNote"
    />

    <h3 v-if="fullName || phoneNumber" :class="$style.personTitle">
      {{ t('viewingDetails.person.title') }}
    </h3>
    <p v-if="fullName" :class="$style.person">
      {{ fullName }}
    </p>

    <PhoneNumber
      v-if="phoneNumber"
      :label="t('viewingDetails.phoneButton.label').toString()"
      :phoneNumber="phoneNumber"
      :revealBlocked="viewingBlocked"
      @mobileNumberClick="emit('phoneNumberClickMobile', PhoneNumberType.VIEWING_PHONE)"
      @numberClick="emit('revealedPhoneNumberClick', PhoneNumberType.VIEWING_PHONE)"
      @numberReveal="emit('phoneNumberReveal', PhoneNumberType.VIEWING_PHONE)"
      @contactPhoneBlock="emit('phoneNumberRevealBlock', PhoneNumberType.VIEWING_PHONE)"
    />
  </div>
</template>

<script lang="ts" setup>
import { Person } from '@smg-real-estate/hg-rets-schema';
import { PhoneNumber } from '@smg-real-estate/seeker-app-shared-components';

import { isWincasaListing } from '@/helpers/ListingData/ListingData';
import { sanitizeHtml } from '@/helpers/Sanitize/Sanitize';
import { wrapUrlsInAnchors } from '@/helpers/TextFormatting/TextFormatting';
import { trackWincasaViewingLinkClick } from '@/views/DetailPage/DetailPage.tracking';

import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { PhoneNumberType } from '../ContactForm/contact-form-tracking-events';

const { t } = useI18n();

defineOptions({
  name: 'ViewingDetails',
});

interface Props {
  viewingPerson: Person;
  listerId?: string;
  viewingBlocked?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  listerId: undefined,
  viewingBlocked: false,
});
const emit = defineEmits<{
  phoneNumberClickMobile: [type: PhoneNumberType];
  revealedPhoneNumberClick: [type: PhoneNumberType];
  phoneNumberReveal: [type: PhoneNumberType];
  phoneNumberRevealBlock: [type: PhoneNumberType];
}>();

const hasViewingDetails = computed((): boolean => {
  return !!viewingNote.value || !!fullName.value || !!phoneNumber.value;
});
const viewingNote = computed((): string | undefined => {
  let note = props.viewingPerson.note;
  if (typeof note !== 'string') {
    return undefined;
  }
  if (isWincasaListing(props.listerId || '')) {
    note = wrapUrlsInAnchors(note);
  }
  return sanitizeHtml(note) || undefined;
});
const fullName = computed((): string | undefined => {
  const givenName = props.viewingPerson.givenName ?? '';
  const familyName = props.viewingPerson.familyName ?? '';
  const fullName = `${givenName} ${familyName}`.trim();
  if (fullName.length > 0) {
    return fullName;
  }
  return undefined;
});
const phoneNumber = computed((): string | undefined => {
  return props.viewingPerson?.phone;
});
const handleViewingNoteClick = (event: Event) => {
  const url = (event.target as HTMLElement).innerHTML;
  trackWincasaViewingLinkClick(url);
};
</script>

<style lang="scss" module>
@import '@/assets/variables';

.viewingNote {
  hyphens: auto;
  word-break: break-word;

  @include body-2;
  p,
  ul {
    margin: 0;
  }
}

.personTitle {
  margin: $space-100 0 $space-100;

  @include body-2;
  @include font-bold;

  line-height: 1.25;
}

.person {
  margin: 0 0 $space-100;
}
</style>
